import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';

interface MovieCardProps {
  movie: Movie;
  variant?: 'default' | 'compact' | 'featured';
}

export default function MovieCard({ movie, variant = 'default' }: MovieCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/detail/${movie.id}`}
        className="group block cursor-pointer"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      >
        <div className="relative aspect-[2/3] rounded-lg overflow-hidden bg-background-200 mb-2">
          <img
            src={movie.imageUrl}
            alt={movie.title}
            title={`${movie.title} - 7777影院高清在线观看`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
            <span className="text-xs text-white/90 line-clamp-2">{movie.description}</span>
          </div>
          {movie.isNew && (
            <span className="absolute top-2 left-2 bg-accent-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap">
              NEW
            </span>
          )}
          <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm text-primary-500 text-xs font-bold px-1.5 py-0.5 rounded whitespace-nowrap">
            {movie.rating}
          </div>
        </div>
        <h3 className="text-sm font-medium text-foreground-100 group-hover:text-primary-400 transition-colors line-clamp-1">
          {movie.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-0.5">{movie.year} · {movie.region}</p>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/detail/${movie.id}`}
        className="group block cursor-pointer"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      >
        <div className="relative aspect-[16/9] rounded-lg overflow-hidden bg-background-200">
          <img
            src={movie.imageUrl}
            alt={movie.title}
            title={`${movie.title} - 7777影院高清在线观看`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
            <div className="flex items-center gap-2 mb-2">
              {movie.isNew && (
                <span className="bg-accent-500 text-white text-[10px] font-bold px-2 py-0.5 rounded whitespace-nowrap">NEW</span>
              )}
              {movie.isHot && (
                <span className="bg-primary-500 text-background-950 text-[10px] font-bold px-2 py-0.5 rounded whitespace-nowrap">HOT</span>
              )}
              <span className="text-xs text-foreground-300">{movie.year}</span>
              <span className="text-xs text-foreground-300">{movie.duration}</span>
            </div>
            <h3 className="text-lg md:text-xl font-bold text-white mb-1">{movie.title}</h3>
            <p className="text-sm text-foreground-300 line-clamp-1">{movie.description}</p>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${movie.id}`}
      className="group block cursor-pointer"
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
    >
      <div className="relative aspect-[2/3] rounded-lg overflow-hidden bg-background-200 mb-3">
        <img
          src={movie.imageUrl}
          alt={movie.title}
          title={`${movie.title} - 7777影院高清在线观看`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500/90 text-white cursor-pointer">
              <i className="ri-play-fill text-sm"></i>
            </span>
          </div>
        </div>
        {movie.isNew && (
          <span className="absolute top-2 left-2 bg-accent-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap">
            NEW
          </span>
        )}
        {movie.isHot && (
          <span className={`absolute ${movie.isNew ? 'top-7' : 'top-2'} left-2 bg-primary-500 text-background-950 text-[10px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap`}>
            HOT
          </span>
        )}
        <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm text-primary-500 text-xs font-bold px-1.5 py-0.5 rounded whitespace-nowrap">
          {movie.rating}
        </div>
      </div>
      <h3 className="text-sm font-medium text-foreground-100 group-hover:text-primary-400 transition-colors line-clamp-1">
        {movie.title}
      </h3>
      <div className="flex items-center gap-1 mt-1">
        <span className="text-xs text-foreground-500">{movie.year}</span>
        <span className="text-[10px] text-foreground-600">·</span>
        <span className="text-xs text-foreground-500">{movie.region}</span>
        <span className="text-[10px] text-foreground-600">·</span>
        <span className="text-xs text-foreground-500 truncate">{movie.tags[0]}</span>
      </div>
    </Link>
  );
}